<script setup lang="ts">
import {Bot} from "@lucide/vue";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {Switch} from "@/core/components/ui/switch";
import BaseAlert from "@/core/components/base/BaseAlert.vue";
import {GiftSendHour} from "@/birthday-gift/interfaces";
import {SEND_HOURS} from "@/birthday-gift/constants/palettes";

const props = defineProps<{
    autoSend: boolean;
    sendHour: GiftSendHour;
    conflictMessage: string | null;
}>();

const emit = defineEmits<{
    (e: 'update:autoSend', value: boolean): void;
    (e: 'update:sendHour', value: GiftSendHour): void;
}>();

const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

const hint = () => props.autoSend
    ? `Ce cadeau part seul à ${hourLabel(props.sendHour)} le matin de l'anniversaire des patients `
        + 'de ce profil. L\'heure s\'applique à tous vos cadeaux automatiques.'
    : 'Désactivé : ce cadeau ne part que si vous le validez depuis les anniversaires du jour.';
</script>

<template>
  <div class="flex flex-col gap-3 border-t border-gray-light pt-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <Bot class="h-5 w-5 shrink-0 text-primary" />
        <div class="min-w-0">
          <div class="text-sm font-semibold">
            Envoi automatique de ce cadeau
          </div>
          <div class="text-xs leading-relaxed text-muted-foreground">
            {{ hint() }}
          </div>
        </div>
      </div>
      <div class="flex items-center gap-3">
        <Select
          :model-value="String(sendHour)"
          :disabled="!autoSend"
          @update:model-value="emit('update:sendHour', Number($event) as GiftSendHour)"
        >
          <SelectTrigger class="w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="hour in SEND_HOURS"
              :key="hour"
              :value="String(hour)"
            >
              {{ hourLabel(hour) }}
            </SelectItem>
          </SelectContent>
        </Select>
        <Switch
          :model-value="autoSend"
          @update:model-value="emit('update:autoSend', $event)"
        />
      </div>
    </div>

    <BaseAlert
      v-if="conflictMessage"
      type="error"
      title="Profils en conflit — envoi automatique impossible"
    >
      {{ conflictMessage }}
    </BaseAlert>
  </div>
</template>
